import React, { useState } from "react";
import { useCreateManualCashSale, CreateCashSaleSchema } from "@/lib/api/risk/manualEntries";
import { useToast } from "@/components/ui/use-toast";
import { z } from "zod";
import { CashSalesForm, CashSalesFormData } from "./CashSalesForm";
import { AcceptedCommodity } from "@/lib/api/risk/profiles";
import { CashSaleStatus, RiskCashSale } from "@/lib/api/risk/cashSales/general";

interface CreateManualCashSalesFormProps {
    onSuccess: () => void;
    onCancel?: () => void;
    showCancelButton?: boolean;
    initialData?: RiskCashSale | null;
}

export function CreateManualCashSalesForm({ onSuccess, onCancel, showCancelButton = true, initialData }: CreateManualCashSalesFormProps) {
    const { toast } = useToast();
    const createManualCashSale = useCreateManualCashSale();
    const [isCreating, setIsCreating] = useState(false);

    const handleSubmit = async (data: CashSalesFormData) => {
        setIsCreating(true);
        try {
            const preData = {
                status: data.status,
                contract_type: data.contract_type,
                contract_number: data.contract_number || "",
                contract_release: data.contract_release || "1",
                location_id: data.location_id ? data.location_id : undefined,
                customer_id: data.customer_id,
                market_zone_id: data.market_zone_id ? data.market_zone_id : undefined,
                trader_id: data.trader_id,

                destination: data.destination ? data.destination : undefined,
                washed_gain_loss: data.washed_gain_loss ? data.washed_gain_loss : undefined,

                add_date: data.add_date,
                start_date: data.start_date || undefined,
                end_date: data.end_date || undefined,
                note: data.note || undefined,
                commodity: data.commodity,
                crop_year: data.crop_year,
                option_month: data.option_month || undefined,
                loads: data.loads || undefined,
                quantity: data.quantity,
                scheduled_quantity: data.scheduled_quantity || undefined,
                balance: data.balance || undefined,
                futures: data.futures || undefined,
                basis: data.basis || undefined,
                price: data.price || undefined,
            };
            // The form validation ensures data is already valid when we get here
            // Just prepare data for API - convert empty strings to undefined for optional fields
            const validationResult = CreateCashSaleSchema.safeParse(preData);

            if (!validationResult.success) {
                const errors = validationResult.error.errors.map(err =>
                    `${err.path.join('.')}: ${err.message}`
                ).join('\n');
                console.error("Failed to parse create cash sale", preData, errors);

                toast({
                    title: "Invalid Data",
                    description: "",
                    variant: "destructive"
                });
                return;
            }

            const createData = validationResult.data;

            const result = await createManualCashSale(createData);

            if (!result.success) {
                throw result;
            }

            toast({ title: "Manual cash sale created successfully" });
            onSuccess();
        } catch (error) {
            console.error("Failed to create manual cash sale", error);
            toast({
                title: "Failed to create manual cash sale",
                description: error instanceof Error ? error.message : "An unexpected error occurred",
                variant: "destructive"
            });
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <CashSalesForm
            mode="create"
            onSubmit={handleSubmit}
            onCancel={onCancel}
            showCancelButton={showCancelButton}
            isSubmitting={isCreating}
            initialData={initialData}
        />
    );
}
